// Sugalarm — avatar crop math (cycle 330.6).
//
// Pure functions, extracted from `AvatarCropper` so they are unit-testable
// WITHOUT a mirrored copy in the test (a mirror is how a test quietly stops
// describing the shipped behavior). The component imports these; the tests
// import these. One implementation.
//
// The frame is a TRUE SQUARE (`aspect-ratio: 1`, unshrinkable), so a single
// `frame` edge length describes it. That is load-bearing: the previous bug
// was the frame's rendered height silently differing from the height the
// math assumed.

export interface Nat {
  w: number;
  h: number;
}

/** Smallest scale that still FILLS the square (no empty area). This was the
 *  old hard minimum — correct for a background, wrong for an avatar. */
export function coverScale(frame: number, nat: Nat): number {
  if (frame <= 0 || nat.w <= 0 || nat.h <= 0) return 1;
  return Math.max(frame / nat.w, frame / nat.h);
}

/** Smallest scale at which the WHOLE photo fits inside the square. This is
 *  the new minimum, and the point of the cycle: a face near the edge of the
 *  source can now be shrunk until all of it is inside the circle. Always
 *  <= coverScale (equal only for an already-square photo). */
export function fitScale(frame: number, nat: Nat): number {
  if (frame <= 0 || nat.w <= 0 || nat.h <= 0) return 1;
  return Math.min(frame / nat.w, frame / nat.h);
}

/** Clamp the image offset within the square.
 *
 *  Generalized for SUB-COVER zoom: when the image is smaller than the frame
 *  on an axis it may still be nudged, but can never be dragged outside the
 *  square — so an uncovered edge is always the deliberate background fill,
 *  never an accident of panning. When it is larger, this reduces exactly to
 *  the original cover clamp. */
export function clampOffset(
  frame: number,
  nat: Nat,
  s: number,
  ox: number,
  oy: number,
): { ox: number; oy: number } {
  const iw = nat.w * s;
  const ih = nat.h * s;
  const loX = Math.min(0, frame - iw);
  const hiX = Math.max(0, frame - iw);
  const loY = Math.min(0, frame - ih);
  const hiY = Math.max(0, frame - ih);
  return {
    ox: Math.min(hiX, Math.max(loX, ox)),
    oy: Math.min(hiY, Math.max(loY, oy)),
  };
}

/** Edge length of the exported SQUARE JPEG.
 *
 *  Capped by `maxLongEdge` (512) AND by the effective source resolution
 *  across the frame (`frame / s`), so the export never upscales. Floored so
 *  a wild zoom can't produce a degenerate image. */
export function exportSize(
  frame: number,
  s: number,
  maxLongEdge: number,
): number {
  if (frame <= 0 || s <= 0) return maxLongEdge;
  return Math.max(64, Math.round(Math.min(maxLongEdge, frame / s)));
}

/** Does the whole photo fit inside the square at scale `s`? (True once the
 *  user has zoomed out to — or past — fit.) */
export function wholePhotoFits(frame: number, nat: Nat, s: number): boolean {
  return nat.w * s <= frame + 0.5 && nat.h * s <= frame + 0.5;
}
